import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AuthService} from '../../../../services/auth/auth.service';
import {LoaderService} from '../../../../services/loader/loader.service';
import {StatisticService} from '../../../../services/statistic/statistic.service';
import {MetaTitle} from '../../../../decorator/meta-title';
import {Paginator} from '../../../../tools/pagination/paginator';
import {ClickReport} from '../../../../models/statistic/click-report';
import { DatePipe } from '@angular/common';
import {PaginatorComponent} from '../../../layout/paginator/paginator.component';
import {RouterLink} from '@angular/router';
import {CsvExportEvent, CsvExportService} from '../../../../services/csv-export/csv-export.service';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-click-report',
  imports: [PaginatorComponent, RouterLink, DatePipe],
  templateUrl: './click-report.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './click-report.component.scss'
})
@MetaTitle('Click report')
export class ClickReportComponent implements OnInit, OnDestroy {

  private filters: any;

  statistics: ClickReport[] = [];
  paginator: Paginator = new Paginator();

  private filtersSubscription: Subscription | null = null;
  private fetchSubscription: Subscription | null = null;
  private exportSubscription: Subscription | null = null;
  private downloadSubscription: Subscription | null = null;

  constructor(
    protected loader: LoaderService,
    private statisticService: StatisticService,
    private auth: AuthService,
    private csvExport: CsvExportService,
    private snackbar: SnackbarService
  ) {
  }

  getTimezone(date: string = ''): string | undefined {
    const timezone = this.auth.getConfig()?.timezone;

    if (timezone === null || timezone === undefined) {
      return undefined;
    }

    // The API stamps each date with the affiliate timezone offset, DST included:
    // reuse it so the displayed time always matches the server-side local time
    const offset = date.match(/[+-]\d{2}:?\d{2}$/);
    if (offset) {
      return offset[0];
    }

    return (timezone < 0 ? '-' : '+') + Math.abs(timezone).toString().padStart(2, '0') + '00';
  }

  ngOnInit(): void {
    this.statistics = [];

    this.exportSubscription = this.statisticService.$exportCsv.subscribe(() => this.downloadCsv());

    this.filtersSubscription = this.statisticService.$filters.subscribe((filters: any) => {
      this.filters = filters;
      this.fetchConversions();
    });
  }

  ngOnDestroy(): void {
    this.filtersSubscription?.unsubscribe();
    this.fetchSubscription?.unsubscribe();
    this.exportSubscription?.unsubscribe();
    this.downloadSubscription?.unsubscribe();
    this.statisticService.$exportProgress.next(null);
    this.loader.stopLoading();
  }

  fetchConversions(): void {
    this.loader.startLoading();

    this.fetchSubscription?.unsubscribe();
    this.fetchSubscription = this.statisticService.fetchClickReport(this.filters, this.paginator)
      .subscribe((statistics: ClickReport[]) => {
        this.statistics = statistics;
        this.loader.stopLoading();
      });
  }

  /**
   * Exported straight from the API: the CSV covers the whole filtered range,
   * not only the page being displayed.
   */
  downloadCsv(): void {
    if (this.loader.isLoading() || !this.statistics.length || this.statisticService.isExporting()) {
      return;
    }

    if (!this.filters?.startDate || !this.filters?.endDate) {
      return;
    }

    this.statisticService.$exportProgress.next({current: 0, total: 0});

    this.downloadSubscription?.unsubscribe();
    this.downloadSubscription = this.csvExport.exportFromApi({
      endpoint: '/statistics/click/download',
      params: this.filters,
      filename: 'click-report.csv'
    }).subscribe({
      next: (event: CsvExportEvent) => {
        if (event.type === 'progress') {
          this.statisticService.$exportProgress.next({current: event.current, total: event.total});
          return;
        }

        this.statisticService.$exportProgress.next(null);

        if (event.truncatedChunks > 0) {
          this.snackbar.warning(
            'The export is incomplete: too many clicks for a single day. Please narrow the date range.'
          );
        }
      },
      error: (error: any) => {
        this.statisticService.$exportProgress.next(null);
        this.snackbar.error('CSV export failed. Please try again.', error);
      }
    });
  }

}
